import React, { useEffect } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployeeGroupScheduledMessageFiles } from '../../../action/scheduled-message-files/load-employee-group-scheduled-message-files';
import {
  announcementsForEmployeeGroupIdSelector,
  announcementsForEmployeeGroupIdStatusSelector,
} from '../../../employee-groups/employee-group-selectors';
import { scheduledMessageFilesForEmployeeGroupIdSelector } from '../../../scheduled-message-files/scheduled-message-files-selectors';
import { loadAnnouncementDeliverySummaries } from '../../actions/announcements/load-announcement-delivery-summaries';
import { loadEmployeeGroupAnnouncements } from '../../actions/announcements/load-employee-group-announcements';
import { updateEmployeeGroupAnnouncement } from '../../actions/announcements/update-employee-group-announcement';

import { UiButton } from '../../../ui/ui-button';
import { AnnouncementComposer } from '../../announcement-composer/announcement-composer';

import { formatDateTime, formatRelativeDate, truncateConversation } from '../../../util/formatters';
import { LanguageCode } from '../../../util/languages';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { MESSAGE_TYPE } from '../../../util/message-types';
import { confirm } from '../../../widgets/confirmation-dialog';
import { LoadingMessage } from '../../../widgets/loading-message';
import { collectMessageToLanguage } from '../../../widgets/message-composer/wrappers/multiple-message-composer-dialog';
import {
  AnnouncementEmail,
  AnnouncementText,
  PendingAnnouncementSection,
} from '../../../widgets/pending-message-section';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  announcements: announcementsForEmployeeGroupIdSelector(state, ownProps.employeeGroupId),
  announcementsStatus: announcementsForEmployeeGroupIdStatusSelector(
    state,
    ownProps.employeeGroupId,
  ),
  scheduledMessageFiles: scheduledMessageFilesForEmployeeGroupIdSelector(
    state,
    ownProps.employeeGroupId,
    ownProps.orgId,
  ),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadEmployeeGroupAnnouncements: () =>
      dispatch(loadEmployeeGroupAnnouncements(ownProps.orgId, ownProps.employeeGroupId)),
    loadAnnouncementDeliverySummaries: (announcementIds: Array<number>) =>
      dispatch(loadAnnouncementDeliverySummaries(announcementIds, ownProps.orgId)),
    loadScheduledMessageFiles: () =>
      dispatch(loadEmployeeGroupScheduledMessageFiles(ownProps.orgId, ownProps.employeeGroupId)),
    cancelAnnouncement: (announcementId: number) =>
      dispatch(
        updateEmployeeGroupAnnouncement(ownProps.orgId, announcementId, {
          cancelledAt: moment.utc().format(),
        }),
      ),
    updateAnnouncement: (
      announcementId: number,
      languageToMessage: Record<LanguageCode, string>,
      languageToSubject: Record<LanguageCode, string>,
    ) =>
      dispatch(
        updateEmployeeGroupAnnouncement(ownProps.orgId, announcementId, {
          languageToMessage,
          languageToSubject,
        }),
      ),
  },
});

type InputProps = {
  orgId: number;
  employeeGroupId: string | number;
  onClose: () => void;
  initialMessage?: string;
  onMessageSent?: () => void;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const EmployeeGroupAnnouncements = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    announcements,
    employeeGroupId,
    orgId,
    onClose,
    initialMessage,
    onMessageSent,
    scheduledMessageFiles,
    actions,
  } = props;

  const [showAllAnnouncements, setShowAllAnnouncements] = React.useState(false);

  const sentAnnouncements = announcements?.filter((a) =>
    moment.utc(a.sentAt).isBefore(moment.utc()),
  );
  const truncatedSentAnnouncements = truncateConversation(
    sentAnnouncements || [],
    (a: Announcement) => a.sentAt,
  );
  const futureAnnouncements = announcements?.filter((a) =>
    moment.utc(a.sentAt).isAfter(moment.utc()),
  );

  useEffect(() => {
    actions.loadEmployeeGroupAnnouncements();
    actions.loadScheduledMessageFiles();
  });

  useEffect(() => {
    const announcementsToShow = (
      (showAllAnnouncements ? sentAnnouncements : truncatedSentAnnouncements) || []
    ).concat(futureAnnouncements || []);
    const announcementIdsToLoad = announcementsToShow?.map((a) => a.announcementId);
    if (announcementIdsToLoad.length) {
      actions.loadAnnouncementDeliverySummaries(announcementIdsToLoad);
    }
  }, [
    actions,
    sentAnnouncements,
    showAllAnnouncements,
    truncatedSentAnnouncements,
    futureAnnouncements,
  ]);

  const handleShowAllAnnouncementsClick = () => {
    setShowAllAnnouncements(true);
  };

  if (!announcements || !scheduledMessageFiles) {
    return <LoadingMessage />;
  }

  const handleEditAnnouncement = (announcement: Announcement) => async () => {
    const languageToMessage: Record<LanguageCode, string> = {
      en: announcement.messageBody,
    };
    let languageToSubject: Record<LanguageCode, string> | undefined;
    if (announcement.subject) {
      languageToSubject = { en: announcement.subject };
    }

    const languagesToSupport = ['en'];
    if (announcement.spanishMessageBody) {
      languagesToSupport.push('es');
      languageToMessage.es = announcement.spanishMessageBody;
      if (announcement.spanishSubject) {
        languageToSubject = languageToSubject
          ? Object.assign(languageToSubject, { es: announcement.spanishSubject })
          : { es: announcement.spanishSubject };
      }
    }

    const returnVal = await collectMessageToLanguage(
      'Edit Announcement',
      `Edit the message for this announcement. It will be sent ${formatRelativeDate(
        announcement.sentAt,
      )}, on ${formatDateTime(announcement.sentAt)}.`,
      {
        enableText: announcement.messageType === MESSAGE_TYPE.TEXT,
        enableEmail: announcement.messageType === MESSAGE_TYPE.EMAIL,
        languageToMessage,
        languageToSubject,
        languagesToSupport,
        buttonClass: 'u-float-right',
        buttonText: 'Save Announcement',
      },
    );

    if (returnVal) {
      actions.updateAnnouncement(
        announcement.announcementId,
        returnVal.languageToMessage,
        returnVal.languageToSubject,
      );
    }
  };
  const handleDeleteAnnouncement = (announcement: Announcement) => async () => {
    if (await confirm('Are you sure you want to cancel this announcement?')) {
      actions.cancelAnnouncement(announcement.announcementId);
    }
  };

  const scheduledMessageFilesArray = scheduledMessageFiles.map((smf) => ({
    announcementId: smf.Announcement && smf.Announcement.announcementId,
    scheduledMessageFile: smf,
  }));
  const urlFileMappingsByAnnouncementId = scheduledMessageFilesArray.reduce(
    (rv: { [announcementId: number]: UrlFileMapping[] }, x) => {
      if (x.announcementId) {
        let announcementIds = rv[x.announcementId];
        if (!announcementIds) {
          // if key is new, create an empty list and push the new item in, otherwise use the existing list
          announcementIds = [];
        }
        announcementIds.push({
          urlKey: x.scheduledMessageFile.dynamicMappingKey,
          fileName: x.scheduledMessageFile.OrganizationFile?.fileName,
          organizationFileId: x.scheduledMessageFile.organizationFileId,
        } as UrlFileMapping);
        rv[x.announcementId] = announcementIds;
      }
      return rv;
    },
    {},
  );

  return (
    <>
      {truncatedSentAnnouncements.length !== sentAnnouncements.length && !showAllAnnouncements && (
        <UiButton
          variant="tertiary"
          size="sm"
          className="u-margin-top u-margin-bottom u-width-100"
          onClick={handleShowAllAnnouncementsClick}
        >
          Show Previous Announcements
        </UiButton>
      )}
      {(showAllAnnouncements ? sentAnnouncements : truncatedSentAnnouncements).map((a) =>
        a.messageType === MESSAGE_TYPE.EMAIL ? (
          <AnnouncementEmail key={a.announcementId} announcement={a} orgId={a.org_id} />
        ) : (
          <AnnouncementText
            key={a.announcementId}
            orgId={a.org_id}
            announcement={a}
            urlFileMappings={urlFileMappingsByAnnouncementId[a.announcementId]}
          />
        ),
      )}
      <PendingAnnouncementSection
        title="Scheduled Announcements"
        icon={<MaterialDesignIcon iconName={ICON_NAMES.EVENT} color="secondary" />}
        messages={futureAnnouncements}
        onEditMessageGenerator={handleEditAnnouncement}
        onCancelMessageGenerator={handleDeleteAnnouncement}
        urlFileMappings={urlFileMappingsByAnnouncementId}
        orgId={orgId}
      />
      <AnnouncementComposer
        initialMessage={initialMessage}
        onMessageSent={onMessageSent}
        orgId={orgId}
        employeeGroupId={employeeGroupId}
        onClose={onClose}
      />
    </>
  );
});
